/* iter55g6 — Extracted hook for the manuscript display settings block.
 *
 * Previously inlined in EditorPanel.jsx (settings state + debounced PATCH
 * back to the project + Ctrl-wheel + Cmd/Ctrl-+/-/0 keyboard shortcuts).
 * Pulling it out keeps EditorPanel.jsx focused on the drawing surface.
 *
 * Contract:
 *   const { settings, persistSettings, scrollAreaRef } =
 *     useManuscriptSettings({ project });
 *
 * `scrollAreaRef` MUST be attached to the scroll container that hosts
 * the manuscript body — that's the element that catches Ctrl+Wheel for
 * zoom.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { projectsApi } from '@/lib/api';
import {
  DEFAULT_MANUSCRIPT_SETTINGS,
  clampSize as clampManuscriptSize,
} from './ManuscriptToolbar';

export function useManuscriptSettings({ project }) {
  const [settings, setSettings] = useState(() => ({
    ...DEFAULT_MANUSCRIPT_SETTINGS,
    ...(project?.manuscript_settings || {}),
  }));

  // Reset local state when the writer switches projects — a stale font
  // size from Project A shouldn't bleed into Project B.
  useEffect(() => {
    setSettings({ ...DEFAULT_MANUSCRIPT_SETTINGS, ...(project?.manuscript_settings || {}) });
  }, [project?.id, project?.manuscript_settings]);

  // Debounced PATCH back to the project so future opens remember it.
  const settingsSaveTimer = useRef(null);
  const persistSettings = useCallback((next) => {
    setSettings(next);
    if (!project?.id) return;
    if (settingsSaveTimer.current) clearTimeout(settingsSaveTimer.current);
    settingsSaveTimer.current = setTimeout(() => {
      projectsApi.update(project.id, { manuscript_settings: next }).catch(() => { /* non-fatal */ });
    }, 500);
  }, [project?.id]);

  // Ctrl/Cmd + wheel = zoom.
  const scrollAreaRef = useRef(null);
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -1 : 1;
      persistSettings({
        ...settings,
        font_size_px: clampManuscriptSize(settings.font_size_px + delta),
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [persistSettings, settings]);

  // Ctrl/Cmd + '+' / '-' / '0' — zoom in, out, reset.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        persistSettings({
          ...settings,
          font_size_px: clampManuscriptSize(settings.font_size_px + 1),
        });
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        persistSettings({
          ...settings,
          font_size_px: clampManuscriptSize(settings.font_size_px - 1),
        });
      } else if (e.key === '0') {
        e.preventDefault();
        persistSettings({
          ...settings,
          font_size_px: DEFAULT_MANUSCRIPT_SETTINGS.font_size_px,
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [persistSettings, settings]);

  return { settings, persistSettings, scrollAreaRef };
}
